import type { QueryClientConfig } from '@tanstack/react-query'
import { QueryClient } from '@tanstack/react-query'
import type { Persister } from '@tanstack/react-query-persist-client'
import type { AxiosInstance, Method } from 'axios'
import axios, { type AxiosRequestConfig } from 'axios'
import merge from 'deepmerge'
import qs from 'qs'
import type { ErrorObject } from 'serialize-error'
import { serializeError } from 'serialize-error'

import { Consts } from '../../configs/consts'
import { sentryCaptureException } from '../../configs/sentry'
import * as T from './api.types'
import { createPersister } from './persister/persister'

export const ApiTypes = T

export class Api {
  private httpClient: AxiosInstance
  queryClient: QueryClient
  queryClientPersister: Persister

  constructor(axiosConfig: AxiosRequestConfig & { authToken?: string } = {}, tanstackConfig: QueryClientConfig = {}) {
    this.httpClient = axios.create(
      merge(
        {
          baseURL: Consts.environments?.[Consts.env!]?.apiBaseUrl,
          headers: { 'Content-Type': 'application/json' },
        },
        axiosConfig,
      ),
    )

    if (axiosConfig.authToken) {
      this.setAuthToken(axiosConfig.authToken)
    }

    this.queryClient = new QueryClient(
      merge(
        {
          defaultOptions: {
            queries: {
              gcTime: 1000 * 60 * 60 * 24,
            },
          },
        },
        tanstackConfig,
      ),
    )
    this.queryClientPersister = createPersister()
  }

  setAuthToken(token?: string) {
    this.httpClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  setBaseUrl(url: string) {
    this.httpClient.defaults.baseURL = url
  }

  async request<ResponseT>(
    path: string,
    method: Method,
    data: any = undefined,
    config?: AxiosRequestConfig & { camelcase?: boolean },
  ): Promise<ResponseT> {
    const { camelcase: _camelcase = true, ...axiosConfig } = config ?? {}

    const requestConfig: AxiosRequestConfig = {
      url: path,
      method,
      data: method !== 'get' ? data : undefined,
      params: method === 'get' ? data : undefined,
      paramsSerializer: params => qs.stringify(params),
      ...axiosConfig,
    }

    if (Consts.isDev) {
      console.info('API request config:', requestConfig)
    }

    const response = await this.httpClient.request<ResponseT>(requestConfig)

    return response.data
  }

  async safeRequest<RequestT extends (...args: any[]) => Promise<any>>(
    requestFn: RequestT,
    ...args: Parameters<RequestT>
  ): Promise<T.SafeRequestResponse<Awaited<ReturnType<RequestT>>>> {
    try {
      const response = await requestFn(...args)

      return { ok: true as const, data: response }
    } catch (error) {
      const e = error as Error & {
        response: {
          data: { errors: { message: string; name: string; data: { errors: { message: string; path: string }[] } }[] }
        }
      }

      sentryCaptureException(e)

      let parsedError: ErrorObject | undefined

      if (e instanceof Error) {
        parsedError = serializeError(e)
      }

      console.info(e)

      return {
        ok: false as const,
        message: e?.response?.data?.errors?.[0]?.message,
        nativeError: parsedError,
        errors: e?.response?.data?.errors,
      }
    }
  }

  /**
   * Authentication
   */
  login = async (params: T.LoginParams) => {
    return this.request<T.LoginResponse>('/users/login', 'post', params)
  }

  logout = async () => {
    return this.request<T.LogoutResponse>('/users/logout', 'post')
  }

  refreshToken = async () => {
    return this.request<T.RefreshTokenResponse>('/users/refresh-token', 'post')
  }

  resetPassword = async (params: T.ResetPasswordParams) => {
    return this.request<T.ResetPasswordResponse>('/users/reset-password', 'post', params)
  }

  forgotPassword = async (params: T.ForgotPasswordParams) => {
    return this.request<T.ForgotPasswordResponse>('/users/forgot-password', 'post', params)
  }

  /**
   * Users
   */
  getUsers = async (params?: T.PayloadFindRequestParams) => {
    return this.request<T.GetUsersResponse>('/users', 'get', params)
  }

  getUser = async (params: T.GetUserParams) => {
    return this.request<T.GetUserResponse>(`/users/${params.id}`, 'get')
  }

  createUser = async (params: T.CreateUserParams) => {
    return this.request<T.CreateUserResponse>('/users', 'post', params)
  }

  updateUser = async (params: T.UpdateUserParams) => {
    return this.request<T.UpdateUserResponse>(`/users/${params.id}`, 'patch', params)
  }

  /**
   * Assets
   */
  getAssets = async (params?: T.PayloadFindRequestParams) => {
    return this.request<T.GetAssetsResponse>('/assets', 'get', params)
  }

  getAsset = async (params: T.GetAssetParams) => {
    return this.request<T.GetAssetResponse>(`/assets/${params.id}`, 'get')
  }

  createAsset = async (params: T.CreateAssetParams) => {
    return this.request<T.CreateAssetResponse>('/assets', 'post', params)
  }

  updateAsset = async (params: T.UpdateAssetParams) => {
    return this.request<T.UpdateAssetResponse>(`/assets/${params.id}`, 'patch', params)
  }

  /**
   * Asset Groups
   */
  getAssetGroups = async (params?: T.PayloadFindRequestParams) => {
    return this.request<T.GetAssetGroupsResponse>('/asset-groups', 'get', params)
  }

  getAssetGroup = async (params: T.GetAssetGroupParams) => {
    return this.request<T.GetAssetGroupResponse>(`/asset-groups/${params.id}`, 'get')
  }

  createAssetGroup = async (params: T.CreateAssetGroupParams) => {
    return this.request<T.CreateAssetGroupResponse>('/asset-groups', 'post', params)
  }

  updateAssetGroup = async (params: T.UpdateAssetGroupParams) => {
    return this.request<T.UpdateAssetGroupResponse>(`/asset-groups/${params.id}`, 'patch', params)
  }

  /**
   * Asset Trackers
   */
  getAssetTrackers = async (params?: T.PayloadFindRequestParams) => {
    return this.request<T.GetAssetTrackersResponse>('/asset-trackers', 'get', params)
  }

  getAssetTracker = async (params: T.GetAssetTrackerParams) => {
    return this.request<T.GetAssetTrackerResponse>(`/asset-trackers/${params.id}`, 'get')
  }

  createAssetTracker = async (params: T.CreateAssetTrackerParams) => {
    return this.request<T.CreateAssetTrackerResponse>('/asset-trackers', 'post', params)
  }

  updateAssetTracker = async (params: T.UpdateAssetTrackerParams) => {
    return this.request<T.UpdateAssetTrackerResponse>(`/asset-trackers/${params.id}`, 'patch', params)
  }

  /**
   * Asset Targets
   */
  getAssetTargets = async (params?: T.PayloadFindRequestParams) => {
    return this.request<T.GetAssetTargetsResponse>('/asset-targets', 'get', params)
  }

  getAssetTarget = async (params: T.GetAssetTargetParams) => {
    return this.request<T.GetAssetTargetResponse>(`/asset-targets/${params.id}`, 'get')
  }

  createAssetTarget = async (params: T.CreateAssetTargetParams) => {
    return this.request<T.CreateAssetTargetResponse>('/asset-targets', 'post', params)
  }

  updateAssetTarget = async (params: T.UpdateAssetTargetParams) => {
    return this.request<T.UpdateAssetTargetResponse>(`/asset-targets/${params.id}`, 'patch', params)
  }

  /**
   * Asset Assignments
   */
  getAssetAssignments = async (params?: T.PayloadFindRequestParams) => {
    return this.request<T.GetAssetAssignmentsResponse>('/asset-assignments', 'get', params)
  }

  getAssetAssignment = async (params: T.GetAssetAssignmentParams) => {
    return this.request<T.GetAssetAssignmentResponse>(`/asset-assignments/${params.id}`, 'get')
  }

  createAssetAssignment = async (params: T.CreateAssetAssignmentParams) => {
    return this.request<T.CreateAssetAssignmentResponse>('/asset-assignments', 'post', params)
  }

  updateAssetAssignment = async (params: T.UpdateAssetAssignmentParams) => {
    return this.request<T.UpdateAssetAssignmentResponse>(`/asset-assignments/${params.id}`, 'patch', params)
  }

  /**
   * Miscellaneous
   */
  getAssetTrackerTempRegistration = async (params: T.GetAssetTrackerTempRegistrationParams) => {
    return this.request<T.GetAssetTrackerTempRegistrationResponse>(`/asset-tracker-temp-registry/${params.id}`, 'get')
  }

  createAssetTrackerTempRegistration = async (params: T.CreateAssetTrackerTempRegistrationParams = {}) => {
    return this.request<T.CreateAssetTrackerTempRegistrationResponse>('/asset-tracker-temp-registry', 'post', params)
  }

  updateAssetTrackerTempRegistration = async (params: T.UpdateAssetTrackerTempRegistrationParams) => {
    return this.request<T.UpdateAssetTrackerTempRegistrationResponse>(
      `/asset-tracker-temp-registry/${params.id}`,
      'patch',
      params,
    )
  }
}
